import React from 'react';
import { createPortal } from 'react-dom';

const SIZE_CLASS = {
    sm: 'max-w-sm',
    md: 'max-w-lg',
    lg: 'max-w-3xl',
    xl: 'max-w-5xl',
};

/**
 * App modal — portals to document.body so `fixed` covers header/sidebar (Ant Layout can trap fixed children).
 * @param {'sm'|'md'|'lg'|'xl'} [size='md'] — panel max width
 */
export const Modal = ({ isOpen, onClose, title, subtitle, children, footer, size = 'md' }) => {
    const titleId = React.useId();
    const [mounted, setMounted] = React.useState(false);
    React.useEffect(() => {
        setMounted(true);
    }, []);

    React.useEffect(() => {
        if (!isOpen) return;
        const onKey = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [isOpen, onClose]);

    React.useEffect(() => {
        if (!isOpen) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [isOpen]);

    if (!isOpen || !mounted || typeof document === 'undefined') return null;

    const maxW = SIZE_CLASS[size] || SIZE_CLASS.md;

    const node = (
        <div
            className="fixed inset-0 z-[2000] flex items-center justify-center p-3 sm:p-6 animate-in fade-in duration-200"
            role="presentation"
        >
            <div
                className="absolute inset-0 bg-slate-950/60 backdrop-blur-md"
                onClick={onClose}
                role="presentation"
                aria-hidden
            />
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby={title ? titleId : undefined}
                className={`relative flex max-h-[min(92dvh,880px)] w-full ${maxW} flex-col overflow-hidden rounded-3xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] shadow-[0_25px_80px_-12px_rgba(15,23,42,0.35)] animate-in zoom-in-95 slide-in-from-bottom-4 duration-300`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="shrink-0 border-b border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] px-6 pb-5 pt-6 sm:px-10 sm:pt-8">
                    <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                            <h3 id={titleId} className="text-xl font-black tracking-tight text-[color:var(--color-text-hero)] sm:text-2xl">
                                {title}
                            </h3>
                            {subtitle ? (
                                <p className="mt-1.5 text-sm leading-relaxed text-[color:var(--color-text-muted)]">{subtitle}</p>
                            ) : null}
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            className="shrink-0 rounded-xl p-2 text-[color:var(--color-text-muted)] transition-colors hover:bg-[color:var(--color-bg-alt)] hover:text-[color:var(--color-text-hero)]"
                        >
                            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2.5"
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>
                    </div>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-6 sm:px-10">
                    <div className="space-y-6">{children}</div>
                </div>

                {footer ? (
                    <div className="shrink-0 border-t border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] px-6 py-5 sm:px-10">
                        {footer}
                    </div>
                ) : null}
            </div>
        </div>
    );

    return createPortal(node, document.body);
};
